import type { AppComboboxGroup, AppComboboxOption, BadgeColor, SelectOption } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { resolveAssignMemberNotice } from '@inquiries/-lib/assign-member-notice'
import { createInquiryAssignFormSchema } from '@inquiries/-lib/inquiryValidation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_WORK_LOCATIONS,
  INQUIRY_ASSIGN_DEFAULTS,
  INQUIRY_ASSIGN_STATUS_COLOR_MAP,
  INQUIRY_ASSIGN_STATUS_DEFS,
  INQUIRY_ASSIGN_STATUSES,
  type WorkLocations,
} from 'shared'
import { getVisibleErrors, touchAll, validateForm } from '@/lib/form-validation'
import { useInquiryAssignMembers } from '../../../../../hooks/useInquiryAssignMembers'
import { useInquiryAssignStatuses } from '../../../../../hooks/useInquiryAssignStatuses'
import { useRoles } from '../../../../../hooks/useRoles'
import { DEFAULT_CONTRACT_TYPES, type InquiryAssignLocal } from '../../../types'

function getStatusColor(name: string): BadgeColor {
  return (INQUIRY_ASSIGN_STATUS_COLOR_MAP[name as keyof typeof INQUIRY_ASSIGN_STATUS_COLOR_MAP] ??
    'secondary') as BadgeColor
}

interface UseAssignSaveDialogParams {
  open: boolean
  onOpenChange: (open: boolean) => void
  inquiryId: number
  assign?: InquiryAssignLocal | null
}

export function useAssignSaveDialog({ open, onOpenChange, inquiryId, assign }: UseAssignSaveDialogParams) {
  const isEdit = !!assign

  // フォーム状態
  const [inquiryAssignStatusId, setInquiryAssignStatusId] = useState<number>(INQUIRY_ASSIGN_STATUSES.SEARCHING)
  const [roleId, setRoleId] = useState<string>('')
  const [memberId, setMemberId] = useState<string>('')
  const [unitPrice, setUnitPrice] = useState('')
  const [assignPersonMonth, setAssignPersonMonth] = useState('')
  const [assignedOn, setAssignedOn] = useState('')
  const [notes, setNotes] = useState('')
  const [workLocations, setWorkLocations] = useState<WorkLocations>({ ...DEFAULT_WORK_LOCATIONS })

  const assignFormSchema = useMemo(() => createInquiryAssignFormSchema(isEdit), [isEdit])

  const allErrors = useMemo(() => {
    const { errors } = validateForm(assignFormSchema, {
      unitPrice: unitPrice ? Number(unitPrice) : null,
      assignPersonMonth: assignPersonMonth ? Number(assignPersonMonth) : null,
      notes: notes || null,
      assignedOn: assignedOn ? `${assignedOn}-01` : null,
    })
    return errors as Record<string, string>
  }, [unitPrice, assignPersonMonth, notes, assignedOn, assignFormSchema])

  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const formErrors = getVisibleErrors(allErrors, touched)
  const hasErrors = Object.keys(formErrors).length > 0

  // ダイアログが開いたときに状態を初期化
  useEffect(() => {
    if (open) {
      setTouched({})
      if (assign) {
        setInquiryAssignStatusId(assign.inquiry_assign_status_id)
        setRoleId(assign.role_id?.toString() ?? '')
        setMemberId(assign.member_id?.toString() ?? '')
        setUnitPrice(assign.unit_price.toString())
        setAssignPersonMonth(assign.assign_person_month.toString())
        setAssignedOn(assign.assigned_on ? assign.assigned_on.slice(0, 7) : '')
        setNotes(assign.notes ?? '')
        setWorkLocations({ ...assign.work_locations })
      } else {
        setInquiryAssignStatusId(INQUIRY_ASSIGN_STATUSES.SEARCHING)
        setRoleId('')
        setMemberId('')
        setUnitPrice(INQUIRY_ASSIGN_DEFAULTS.UNIT_PRICE)
        setAssignPersonMonth(INQUIRY_ASSIGN_DEFAULTS.PERSON_MONTH)
        setAssignedOn('')
        setNotes('')
        setWorkLocations({ ...DEFAULT_WORK_LOCATIONS })
      }
    }
  }, [open, assign])

  // マスタデータ取得
  const { data: assignStatusesData } = useInquiryAssignStatuses()
  const assignStatuses = assignStatusesData?.inquiry_assign_statuses ?? []

  const {
    data: assignMembersData,
    isPending: isMembersLoading,
    isError: isMembersError,
  } = useInquiryAssignMembers({ inquiryId })

  const { data: rolesData, isPending: isRolesLoading } = useRoles()

  // 役割グループ（APIから取得してgroup_nameでグルーピング）
  const roleGroups = useMemo<AppComboboxGroup[]>(() => {
    const roles = rolesData?.roles ?? []
    const groupMap = new Map<string, AppComboboxOption[]>()
    for (const role of roles) {
      const groupOptions = groupMap.get(role.group_name) ?? []
      groupOptions.push({ value: role.id.toString(), label: role.name })
      groupMap.set(role.group_name, groupOptions)
    }
    return Array.from(groupMap, ([label, options]) => ({ label, options }))
  }, [rolesData])

  // アサイン状況のBadgeSelectField用オプション
  const statusBadgeOptions: SelectOption[] = useMemo(
    () =>
      assignStatuses.map((s) => ({
        value: s.id.toString(),
        label: s.name,
        color: getStatusColor(s.name),
      })),
    [assignStatuses],
  )

  // 現在のステータス情報
  const currentStatus = useMemo(() => {
    const status = assignStatuses.find((s) => s.id === inquiryAssignStatusId)
    const fallbackName = assign?.inquiry_assign_status_name ?? INQUIRY_ASSIGN_STATUS_DEFS[0].name
    const name = status?.name ?? fallbackName
    return {
      label: name,
      color: getStatusColor(name),
    }
  }, [assignStatuses, inquiryAssignStatusId, assign])

  const handleClose = () => {
    onOpenChange(false)
  }

  // メンバー選択肢（APIから取得）
  const memberOptions = useMemo<AppComboboxOption[]>(() => {
    const apiMembers: AppComboboxOption[] = (assignMembersData?.members ?? []).map((m) => ({
      value: m.id.toString(),
      label: m.name,
      data: { employeeNumber: m.employeeNumber, employeeTypeName: m.employeeTypeName },
    }))

    // 編集時、既存メンバーがAPIリストに含まれていなければ動的に追加
    if (assign?.member_id && assign.member_name) {
      const existingMemberId = assign.member_id.toString()
      const alreadyExists = apiMembers.some((m) => m.value === existingMemberId)
      if (!alreadyExists) {
        apiMembers.push({
          value: existingMemberId,
          label: assign.member_name,
          data: { employeeNumber: assign.employee_number, employeeTypeName: assign.employee_type_name },
        })
      }
    }

    return apiMembers
  }, [assignMembersData, assign])

  // 編集時に補った既存メンバーを数えないよう、API の候補数で判定する
  const memberNotice = resolveAssignMemberNotice({
    candidateCount: assignMembersData?.members.length,
    isError: isMembersError,
  })

  // 選択中のメンバー情報を取得
  const selectedMember = useMemo(() => {
    if (!memberId) return null
    return memberOptions.find((m) => m.value === memberId) ?? null
  }, [memberId, memberOptions])

  const selectedMemberName = selectedMember?.label ?? null
  const selectedEmployeeTypeName =
    (selectedMember?.data as { employeeTypeName?: string | null })?.employeeTypeName ?? null

  const withTouch = <T>(key: string, setter: (v: T) => void) => {
    return (v: T) => {
      setter(v)
      setTouched((prev) => ({ ...prev, [key]: true }))
    }
  }

  const validate = useCallback((): boolean => {
    setTouched(touchAll(assignFormSchema))
    return Object.keys(allErrors).length === 0
  }, [allErrors, assignFormSchema])

  // フォーム値をInquiryAssignLocal形式にまとめる
  const buildAssignData = (): Omit<InquiryAssignLocal, 'id' | 'inquiry_id' | 'updated_at' | 'created_at'> => {
    const roleIdNum = roleId ? Number(roleId) : null
    const roleName = roleIdNum
      ? (rolesData?.roles.find((r) => r.id === roleIdNum)?.name ?? assign?.role_name ?? null)
      : null

    const memberIdNum = memberId ? Number(memberId) : null

    return {
      member_id: memberIdNum,
      member_name: memberIdNum ? (selectedMemberName ?? 'TBA') : null,
      employee_number: null,
      employee_type_id: null,
      employee_type_name: null,
      candidate_member_id: null,
      candidate_member_name: null,
      candidate_member_employee_number: null,
      division_id: null,
      division_name: null,
      short_division_name: null,
      role_id: roleIdNum,
      role_name: roleName,
      unit_price: Number(unitPrice) || 0,
      assigned_on: assignedOn ? `${assignedOn}-01` : '',
      assign_person_month: Number(assignPersonMonth) || 0,
      sales_status_id: null,
      sales_status_name: null,
      sales_recording_group_id: null,
      sales_recording_group_name: null,
      inquiry_assign_status_id: inquiryAssignStatusId,
      inquiry_assign_status_name: currentStatus.label,
      is_onsite: workLocations.onsite,
      is_offsite: workLocations.offsite,
      is_remote: workLocations.remote,
      notes: notes.trim() || null,
      contract_types: { ...DEFAULT_CONTRACT_TYPES },
      work_locations: { ...workLocations },
    }
  }

  return {
    isEdit,
    // フォーム値
    inquiryAssignStatusId,
    setInquiryAssignStatusId,
    roleId,
    setRoleId,
    memberId,
    setMemberId,
    unitPrice,
    setUnitPrice: withTouch('unitPrice', setUnitPrice),
    assignPersonMonth,
    setAssignPersonMonth: withTouch('assignPersonMonth', setAssignPersonMonth),
    assignedOn,
    setAssignedOn: withTouch('assignedOn', setAssignedOn),
    notes,
    setNotes: withTouch('notes', setNotes),
    workLocations,
    setWorkLocations,
    selectedEmployeeTypeName,
    // マスタデータ
    statusBadgeOptions,
    roleGroups,
    isRolesLoading,
    memberOptions,
    isMembersLoading,
    memberNotice,
    // バリデーション
    formErrors,
    hasErrors,
    validate,
    // ハンドラ
    handleClose,
    buildAssignData,
  }
}
